import clsx from "clsx";
import { PropsWithChildren, useCallback, useEffect, useRef, useState } from "react";

// Fades the shadow strip's own top/bottom edges to transparent so it reads as
// a soft vignette instead of a hard-edged rectangle cutting across the content.
const EDGE_SHADOW_MASK = "linear-gradient(to bottom, transparent, black 20%, black 80%, transparent)";
const edgeShadowMaskStyle: React.CSSProperties = {
  maskImage: EDGE_SHADOW_MASK,
  WebkitMaskImage: EDGE_SHADOW_MASK,
};

/**
 * Wraps horizontally-scrollable content and paints a soft shadow at whichever
 * edge(s) still have more content to reveal, so users get a purely visual
 * cue instead of relying on scrollbars/zoom to notice there's more to see.
 *
 * A background-image scroll-shadow (the classic CSS-only trick) doesn't work
 * when the scrollable content has opaque cells with gaps between them (e.g. a
 * grid of table cells/inputs) -- it paints behind the content, so opaque
 * cells punch holes in it wherever they sit. Painting the shadows as
 * absolutely-positioned overlays on top of everything avoids that, at the
 * cost of tracking scroll position ourselves.
 */
export const HorizontalScrollHint: React.FC<PropsWithChildren> = ({ children }) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);

  const updateShadows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeftShadow(el.scrollLeft > 0);
    setShowRightShadow(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  // Measuring the node as soon as it's attached (via the ref callback, at
  // commit time) rather than in a useEffect avoids an extra post-paint
  // render pass just to get the initial shadow state right.
  const setScrollRef = useCallback(
    (el: HTMLDivElement | null) => {
      scrollRef.current = el;
      if (el) updateShadows();
    },
    [updateShadows],
  );

  useEffect(() => {
    const scrollEl = scrollRef.current;
    const contentEl = contentRef.current;
    if (!scrollEl || !contentEl) return;
    scrollEl.addEventListener("scroll", updateShadows);
    // Watch both the scroll container (e.g. a resizable panel changing width)
    // and the content node itself (e.g. scrollWidth growing as form values
    // populate max-content cells, with the container's own size unchanged) --
    // either can change whether there's more to scroll in either direction.
    const resizeObserver = new ResizeObserver(updateShadows);
    resizeObserver.observe(scrollEl);
    resizeObserver.observe(contentEl);
    return () => {
      scrollEl.removeEventListener("scroll", updateShadows);
      resizeObserver.disconnect();
    };
  }, [updateShadows]);

  return (
    <div className="relative">
      <div ref={setScrollRef} className="overflow-x-auto">
        <div ref={contentRef}>{children}</div>
      </div>
      <div
        className={clsx(
          "pointer-events-none absolute inset-y-0 left-0 w-4 bg-gradient-to-r from-black/15 to-transparent transition-opacity",
          showLeftShadow ? "opacity-100" : "opacity-0",
        )}
        style={edgeShadowMaskStyle}
      />
      <div
        className={clsx(
          "pointer-events-none absolute inset-y-0 right-0 w-4 bg-gradient-to-l from-black/15 to-transparent transition-opacity",
          showRightShadow ? "opacity-100" : "opacity-0",
        )}
        style={edgeShadowMaskStyle}
      />
    </div>
  );
};
